import * as React from "react";
import { Gender } from "../../../../../../react-core/Enums/Gender";
import { BankAccountOwner } from "../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { MaritalStatus } from "../../../../../../Wrap/Constants/Enums/Domain/MaritalStatus";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { ClientRegularPayment } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ClientRegularPayment";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ClientSinglePayment } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ClientSinglePayment";
import { ThirdPartyPaymentModel } from "../../../../../../Wrap/Models/Models/Shared/ThirdPartyPaymentModel";
import { DepositMethod } from "../../../../../../Wrap/Constants/Enums/Domain/DepositMethod";
import { SourceOfWealthOption } from "../../../../../../Wrap/Constants/Enums/Apply/SourceOfWealthOption";
import { ThirdPartyType } from "../../../../../../Wrap/Constants/Enums/ThirdPartyType";
import { CustomerStatus } from "../../../../../../Wrap/Constants/Enums/Domain/CustomerStatus";
import { MoneyPurchaseAnnualAllowanceSource } from "../../../../../../Wrap/Constants/Enums/Apply/MoneyPurchaseAnnualAllowanceSource";
import { RegularDepositMethod } from "../../../../../../Wrap/Constants/Enums/Domain/RegularDepositMethod";
import { CedingType } from "../../../../../../Wrap/Constants/Enums/Apply/CedingType";
import { DirectDebitMethod } from "../../../../../../Wrap/Constants/Enums/Domain/DirectDebitMethod";
import { PensionTransfer } from "../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { TransferType } from "../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { PensionType } from "../../../../../../Wrap/Constants/Enums/Domain/PensionType";
import { DisinvestmentStrategyType } from "../../../../../../Wrap/Constants/Enums/Client/DisinvestmentStrategyType";
import { UserClaim } from "../../../../../../Wrap/Constants/Enums/UserClaim";
import { Permission } from "../../../../../Shared/Permission/Permission";

export interface TransferOrDrawdown extends PensionTransfer {
    PensionTransfersIndex?: number;
    IsInternal?: boolean;
}

export const MaxGiaCount = 5;

export const NBSteppers = {
    fullClientDetail: "Full client details",
    wrapperSelection: "Wrapper selection",
    paymentsIn: "Payments in",
    adviserCharges: "Adviser charges",
    investmentSelection: "Investment selection",
    incomePaymentDetails: "Income payment details",
    disinvestmentStrategy: "Disinvestment strategy",
    review: "Review",
    doc: "Documents & declarations",
    regularWithdrawals: "Regular withdrawal",
    drawdownDetail: "Drawdown detail",
    comfirmation: "Confirmation",
    withdrawal: "Withdrawals",
    SIPP: "SIPP",
    ISA: "ISA Stocks & Shares",
    PP: "Personal Portfolio",
    wrapCash: "Cash Account",
    openBondInLegacyWizard: "Open Onshore/Offshore bond",
    legacyWizard: "New business existing client",
    legacyProduct: "SIPP & Bond detail",
    additionalProductDetails: "Additional product details",
};

export const regularDepositMethodArrays = [
    RegularDepositMethod.Cheque,
    RegularDepositMethod.BACS,
    RegularDepositMethod.DirectDebit,
    RegularDepositMethod.ProductWrapperCash,
];

export const regularDepositMethodArraysMap = {
    [RegularDepositMethod.Cheque]: "Cheque",
    [RegularDepositMethod.BACS]: "BACS/CHAPS",
    [RegularDepositMethod.DirectDebit]: "Direct Debit",
    [RegularDepositMethod.ProductWrapperCash]: "Wrap Cash",
};

export const depositMethodArrays = [
    DepositMethod.Cheque,
    DepositMethod.BACS,
    DepositMethod.DirectDebit,
    DepositMethod.WrapCash,
];
export const depositMethodArraysMap = {
    [DepositMethod.Cheque]: "Cheque",
    [DepositMethod.BACS]: "BACS/CHAPS",
    [DepositMethod.DirectDebit]: "Direct Debit",
    [DepositMethod.WrapCash]: "Cash Account",
};

export const DisinvestmentStrategyTypeArray = [
    DisinvestmentStrategyType.Proportional,
    DisinvestmentStrategyType.Lifo,
    DisinvestmentStrategyType.LVS,
];

export const DisinvestmentStrategyTypeStringMap = (label: string) => {
    switch (label.toLowerCase()) {
        case "proportional":
            return DisinvestmentStrategyType.Proportional;
        case "lvs":
            return DisinvestmentStrategyType.LVS;
        case "lifo":
            return DisinvestmentStrategyType.Lifo;
        default:
            return DisinvestmentStrategyType.Proportional;
    }
};

export const FrequencyTypeArrays = [Frequency.Monthly, Frequency.Quarterly, Frequency.HalfYearly, Frequency.Yearly];

export const FrequencyArraysMap = {
    [Frequency.Monthly]: "Monthly",
    [Frequency.Quarterly]: "Quarterly",
    [Frequency.HalfYearly]: "Half yearly",
    [Frequency.Yearly]: "Yearly",
};
export const BankAccountOwnerArrays = [
    BankAccountOwner.Individual,
    BankAccountOwner.Partner,
    BankAccountOwner.Joint,
    BankAccountOwner.Employer,
    BankAccountOwner.ThirdParty,
];

export const BankAccountOwnerMap = {
    [BankAccountOwner.Individual]: "Individual",
    [BankAccountOwner.Partner]: "Partner",
    [BankAccountOwner.Joint]: " Joint",
    [BankAccountOwner.Employer]: "Employer",
    [BankAccountOwner.ThirdParty]: "Third-party",
};

export const AdviceType = [
    {
        key: "nonDiscretionary",
        value: "Non-discretionary",
    },
    {
        key: "Discretionary",
        value: "Discretionary",
    },
];

export const GenderOptions = [Gender.Male, Gender.Female];

export const GenderOptionsMap = {
    [Gender.Male]: "Male",
    [Gender.Female]: "Female",
};

export const CustomerStatuses = {
    title: "Customer Status",
    options: [CustomerStatus.Active, CustomerStatus.Inactive, CustomerStatus.Deceased],
    optionsMap: {
        [CustomerStatus.Active]: "Active",
        [CustomerStatus.Inactive]: "Inactive",
        [CustomerStatus.Deceased]: "Deceased",
    },
};

export const Marital = {
    title: "Marital status",
    options: [
        MaritalStatus.Single,
        MaritalStatus.MarriedOrCivilPartner,
        MaritalStatus.Separated,
        MaritalStatus.Divorced,
        MaritalStatus.Widowed,
    ],
    optionsMap: {
        [MaritalStatus.Divorced]: "Divorced / Dissolved civil partnership",
        [MaritalStatus.MarriedOrCivilPartner]: "Married / Civil partnership",
        [MaritalStatus.Single]: "Single",
        [MaritalStatus.Separated]: "Separated",
        [MaritalStatus.Widowed]: "Widowed / Surviving civil partner",
    },
};

export const emptyClientRegularPayment: ClientRegularPayment = {
    Frequency: Frequency.Monthly,
    Money: { Amount: null, Currency: CurrencyCode.GBP },
    IsContinue: null,
    ContinueUntil: null,
    FirstCollectionDate: null,
};

export const emptyClientSinglePayment: ClientSinglePayment = {
    BankAccountNumber: null,
    Money: { Amount: null, Currency: CurrencyCode.GBP },
    PaymentMethod: null,
    PaymentDate: null,
};

export const emptyThirdPartyRegularPayment: ThirdPartyPaymentModel = {
    Money: { Amount: null, Currency: CurrencyCode.GBP },
    Frequency: Frequency.Monthly,
    IsContinue: null,
    ContinueUntil: null,
    FirstCollectionDate: null,
};

export const emptyThirdPartySinglePayment: ThirdPartyPaymentModel = {
    Money: { Amount: null, Currency: CurrencyCode.GBP },
    PaymentMethod: null,
    PaymentDate: null,
};

export const CedingTypeArrays = [
    CedingType.Stakeholderpension,
    CedingType.PersonalPension,
    CedingType.SelfInvestedPersonalPension,
    CedingType.RetirementAnnuityContract,
    CedingType.GroupPersonalPension,
    CedingType.GroupStakeholderPension,
    CedingType.ExecutivePensionPlan,
    CedingType.SmallSelfAdministeredscheme,
    CedingType.Section32,
    CedingType.OtherMoneyPurchase,
    CedingType.DefinedBenefit,
    CedingType.QROPSROPSscheme,
];

export const CedingTypeMap = {
    [CedingType.Stakeholderpension]: "Stakeholder pension",
    [CedingType.PersonalPension]: "Personal Pension",
    [CedingType.SelfInvestedPersonalPension]: "Self-invested personal pension",
    [CedingType.RetirementAnnuityContract]: "Retirement Annuity Contract (Section 22/226)",
    [CedingType.GroupPersonalPension]: "Group Personal Pension",
    [CedingType.GroupStakeholderPension]: "Group Stakeholder Pension",
    [CedingType.ExecutivePensionPlan]: "Executive Pension Plan",
    [CedingType.SmallSelfAdministeredscheme]: "Small self-administered scheme",
    [CedingType.Section32]: "Section 32",
    [CedingType.OtherMoneyPurchase]: "Other Money Purchase",
    [CedingType.DefinedBenefit]: "Defined Benefit (Final Salary)",
    [CedingType.QROPSROPSscheme]: "QROPS/ROPS scheme",
};

export const TransferTypeMap = {
    [TransferType.Cash]: "Cash",
    [TransferType.InSpecie]: "Re-register assets",
};

export class ContentsTable {
    public static None = "None";
    public static DirectDebitMethodInformation1 =
        "An online Direct Debit can only be set up if the client is present at the point the bank details are entered.";
    public static DirectDebitMethodInformation2 =
        "If the client is not present you can still enter the bank details here but you must obtain a signed Direct Debit form from the client and send it to us.";
    public static DirectDebitMethodInformation3 =
        "In that case, we will not start making Direct Debit collections until we have received the signed form";
    public static DirectDebitMethodInformation4 =
        "A Direct Debit mandate is already in force for this client and therefore we can begin collections from this bank account without needing a further instruction from your client.";
}

export const MoneyPurchaseAnnualAllowanceSourceArray = [
    MoneyPurchaseAnnualAllowanceSource.NotSpecified,
    MoneyPurchaseAnnualAllowanceSource.UncrystallisedFundsPcls,
    MoneyPurchaseAnnualAllowanceSource.StandaloneLumpSum,
    MoneyPurchaseAnnualAllowanceSource.FlexiAccessIncomePayment,
];

export const MoneyPurchaseAnnualAllowanceSourceArrayMap = {
    [MoneyPurchaseAnnualAllowanceSource.NotSpecified]: "Unknown",
    [MoneyPurchaseAnnualAllowanceSource.UncrystallisedFundsPcls]: "Uncrystallised pension saving lump sum payment",
    [MoneyPurchaseAnnualAllowanceSource.StandaloneLumpSum]: "Standalone lump sum payment",
    [MoneyPurchaseAnnualAllowanceSource.FlexiAccessIncomePayment]: "Flexi-access drawdown payment",
};

export const SourceOfWealthArrary = [
    SourceOfWealthOption.Salary,
    SourceOfWealthOption.DivorceSettlement,
    SourceOfWealthOption.Gift,
    SourceOfWealthOption.CompensationPayment,
    SourceOfWealthOption.LotteryAndOtherWinnings,
    SourceOfWealthOption.PolicyClaimOrMaturity,
    SourceOfWealthOption.Inheritance,
    SourceOfWealthOption.Investments,
    SourceOfWealthOption.PropertySale,
    SourceOfWealthOption.CompanySale,
    SourceOfWealthOption.Savings,
    SourceOfWealthOption.Other,
];

export const SourceOfWealthArraryMap = {
    [SourceOfWealthOption.Salary]: "Salary/Bonus/Employer",
    [SourceOfWealthOption.DivorceSettlement]: "Divorce settlement",
    [SourceOfWealthOption.Gift]: "Gift",
    [SourceOfWealthOption.CompensationPayment]: "Compensation payment",
    [SourceOfWealthOption.LotteryAndOtherWinnings]: "Lottery/betting win",
    [SourceOfWealthOption.PolicyClaimOrMaturity]: "Policy claim/maturity",
    [SourceOfWealthOption.Inheritance]: "Inheritance",
    [SourceOfWealthOption.Investments]: "Sale of investments",
    [SourceOfWealthOption.PropertySale]: "Sale of property",
    [SourceOfWealthOption.CompanySale]: "Sale of company",
    [SourceOfWealthOption.Savings]: "Savings",
    [SourceOfWealthOption.Other]: "Other (please give details below)",
};

export const ThirdPartyTypeArray = [
    ThirdPartyType.Individual,
    ThirdPartyType.Trust,
    ThirdPartyType.Charity,
    ThirdPartyType.Corporate,
    ThirdPartyType.ClubOrAssociation,
];

export const ThirdPartyTypeArrayMap = {
    [ThirdPartyType.Individual]: "Individual (includes spouse)",
    [ThirdPartyType.Trust]: "Trust",
    [ThirdPartyType.Charity]: "Charity",
    [ThirdPartyType.Corporate]: "Corporate",
    [ThirdPartyType.ClubOrAssociation]: "Club/association",
};

export const PayeeType = {
    Client: "Client",
    ThirdParty: "Third party",
    Employer: "Employer",
};

export const BankAccountOwnerPayeeTypeMap = {
    [BankAccountOwner.Individual]: [PayeeType.Client],
    [BankAccountOwner.Joint]: [PayeeType.Client],
    [BankAccountOwner.Partner]: [PayeeType.Client],
    [BankAccountOwner.Employer]: [PayeeType.Employer, PayeeType.Client],
    [BankAccountOwner.ThirdParty]: [PayeeType.ThirdParty],
};

export const DirectDebitMethodArray = [DirectDebitMethod.Online, DirectDebitMethod.Paper];

export const DirectDebitMethodArrayMap = {
    [DirectDebitMethod.Online]: "Online",
    [DirectDebitMethod.Paper]: "Paper",
};

export const ContinueUntil = ["Until notified", "Until date"];

export const DirectDebitOptionsLabels = {
    directDebitOptionsInfoText:
        "An online Direct Debit can only be set up if the client is present at the point the bank details are entered. If the client is not present you can still enter the bank details here but you must obtain a signed Direct Debit form from the client and send it to us. In that case, we will not start making Direct Debit collections until we have received the signed form",
    directDebitAcceptTerms: "I confirm that I understand and agree to the Online Direct Debit terms",
    directDebitRequiredTerms: "You must complete the required declaration",
};

export const nbSippIncomePaymentDetailsLabels = {
    regularIncomePayment: "Regular income payment",
    taxableIncomeRequiredInAddition: "Is taxable income required in addition to the tax-free lump sum?",
    targetSpecificArrangements: "Do you wish to target specific arrangements?",
    takeDifferentInitialIncomePayment: "Do you wish to take a different initial income payment?",
    grossInitialIncomePayment: "Gross initial income payment",
    maximumInitialIncomePayment: "Maximum for initial income payment",
    maximumIncomePerPayment: "Maximum income per payment",
    maximumAvailablePerPayment: "Maximum available per payment",
    grossIncomePerPayment: "Gross income per payment",
    grossIncomePerPaymentFromPension: "Gross income per payment from the pension",
    incomeFrequency: "Income frequency",
    firstPaymentDate: "First payment date",
    endDate: "End date",
    untilNotified: "Until notified",
    enterADate: "Enter a date",
    calculateEstimatedPaye: "Calculate estimated PAYE",
    estimatedPayeTax: "Estimated PAYE tax",
    estimatedNetPayment: "Estimated net payment",
    taxCode: "Tax Code",
    taxBasis: "Basis",
    oneOffIncomePayment: "One-off income payment",
    takeOneOffIncomePayment: "Do you wish to take a one-off income payment?",
    maximumGrossOneOffIncomePayment: "Maximum gross one-off income payment",
    crystallisation: "Crystallisation",
    capped: "Capped",
    flexi: "Flexi",
    from: "From",
    amount: "Amount",
    type: "Type",
    member: "Member",
    inherited: "Inherited",
    gadMaximum: "GAD maximum",
    incomeTakenToDate: "Income taken to date",
    lastMandatoryReview: "Last mandatory review",
    maximumIncomePaymentForRemainderOfYear: "Maximum income payment for remainder of pension year",
    maximumIncomePaymentForFutureYears: "Maximum income payment for future pension years",
    incomePerPaymentForCurrentPensionYear: "Income per payment for current pension year",
    incomePerPaymentForFuturePensionYears: "Income per payment for future pension years",
    MaximumIncomePerPayment: "Maximum income payment per payment",
    incomePerPayment: "Income per payment",
    takeFullValue: "Take full value",
    flexiArrangementsExhausted: "Please tick to confirm your client understands this and is happy to proceed",
    chooseAssetSelectionMethod: "Choose asset selection method",
    proportionallyAcrossAllAssets: "Proportionally across all assets",
    fromSpecifiedAssets: "From specified assets",
    chooseAssetsHeader: "Disinvestment from post-retirement to meet income payments",
    nextYearsGadMaximum: "Next years GAD maximum",
    cappedPlusFlexiTotals: "Capped plus flexi totals",
    nextFlexiReviewDate: "Next flexi income review date",
    frequencyOfFlexiIncomeReview: "Frequency of flexi income review",
    paymentMethod: "Payment method",
    investment: "Investment",
    currentUnitsHeld: "Current units held",
    currentEstimatedValue: "Current estimated value",
    estimatedValueToSell: "Estimated value to sell",
    sumOfValueToSell: "Percentage of income payment (total)",
    selectBankAccount: "Select bank account",
    splitAcrossTwoBankAccounts: "Split across two bank accounts",
    bankAccount1: "Bank account 1",
    bankAccount2: "Bank account 2",
    addBankAccount: "Add bank account",
    portionOfPcls: "Portion of PCLS",
    proportionOfIncome: "Proportion of income",
    bankAccountNotValidated:
        "Please note this bank account is not validated - it must have been validated by the time we do the crystallisation.",
};

export const nbSippIncomePaymentDetailsMessages = {
    grossInitialIncomePaymentRequired: "Please enter gross initial income payment",
    grossIncomePerPaymentRequired: "Please enter gross income per payment",
    grossIncomePerPaymentFromPensionRequired: "Please enter gross income per payment from the pension",
    incomeFrequencyRequired: "Please enter income frequency",
    firstPaymentDateRequired: "Please enter first payment date",
    enterADateRequired: "Please enter a date",
    oneOffIncomePaymentRequired: "Please enter one-off income payment",
    incomePerPaymentForCurrentPensionYearRequired: "Please enter income per payment for current pension year",
    incomePerPaymentForFuturePensionYearsRequired: "Please enter income per payment for future pension years",
    incomePerPaymentRequired: "Please enter income per payment",
    flexiArrangementsExhausted:
        "The income specified will exhaust one or more of your client's flexi access arrangements within a year",
    messagesGrossInitialIncomeOver: "Gross initial income payment must not be greater than maximum",
    grossIncomePerPaymentOver:
        "Please enter an income amount that is greater than zero and less than or equal to the maximum income per payment.",
    grossAvailablePerPaymentOver:
        "Please enter an income amount that is greater than zero and less than or equal to the maximum available per payment.",
    firstPaymentDateUnder15: "The date selected must be at least 15 working days from today",
    firstPaymentDateUnder10: "The date selected must be at least 10 working days from today",
    endDateUnder: "End date must be equal to or later than the first payment date",
    dateOver28: "Date must be between 1-28 of the month",
    targetSpecificArrangementsMessage: (
        <div className="warning-message">
            Where the income is on a target basis, information must be provided to the user that any income entered form
            an arrangement here will:
            <div>
                Not automatically be set up if it is from a capped arrangement (this is for illustration purposes only)
            </div>
            <div>
                Will only be automatically set up from a flexi arrangement if:
                <ul>
                    <li>Associated with a request to move to drawdown</li>
                    <li>Where transfer</li>
                    <ul>
                        <li>the amount requested is equal to or less than the transfer value</li>
                        <li>only 1 HMRC arrangement is confirmed upon transfer</li>
                        <li>has not been converted for flexi from capped upon receipt into the pension scheme</li>
                    </ul>
                    <li>the bank account has been nominated and verified for withdrawals</li>
                </ul>
            </div>
        </div>
    ),
    standardAlgorithmCappedMessage:
        "SL to provide content regarding the maximum gross annual income and the income paid to date",
    inheritedFundMessage:
        "SL to provide content around whether the inherited drawdowns should be targeted or excluded and they should consider the use of the standard algorithm or targeting arrangements",
    etiProRataMessage: "SL TO CONFIRM MESSAGE",
    percentageOver100: "value can't exceed 100%",
    percentageUnder0: "At least 0%",
    not100PercentMessage:
        "Please enter values against the assets from which the income payment will be made that total 100%",
    cannotBeGreaterThanMaximumIncomePaymentForRemainderOfYear:
        "Income per payment for current pension year cannot be more than the maximum",
    cannotBeGreaterThanMaximumIncomePaymentForFutureYears:
        "Income per payment for future pension years cannot be more than the maximum",
    cannotBeGreaterThanMaximumPerIncomePayment: "Income per payment cannot be more than maximum",
    nextFlexiReviewDateRequired: "Please enter next flexi income review date",
    nextFlexiReviewDateUnder: "Date must be 20 days or more in the future",
    frequencyOfFlexiIncomeReviewUnderOver: "Frequency must be between 1 and 60",
    totalPortionOfPCLSNot100: "Total portion of PCLS must be 100%",
    totalProportionOfIncomeNot100: "Total proportion of income must be 100%",
    grossIncomeNotSumOfArrangementsIncome: "Total of arrangments income must equal gross income",
};

export const nbPaymentsInMessages = {
    atLeastOneContribution:
        "Please make a contribution to this product wrapper to continue. To remove this product type, please navigate back to the wrapper selection step",
};

export const WrapperSelectionContents = {
    clientNameMessage: (firstName: string, middleName: string, familyName: string) =>
        `${firstName} ${middleName || ""} ${familyName} has been successfully added as a client`,
};

export const NewTpsInvestmentsMessage = {
    messagePart1: "To proceed into a TPS Model, you must ensure a mandate is set up via the ",
    messagePart2: " page once the sub account is active.",
    link: (
        <Permission
            userClaim={UserClaim.AdviserPortalDluMandatesSearchCanView}
            replaceChildren={"Setup and search mandate"}
        >
            <a className="inline-link" target="_blank" href="/mandates/searchandsetup/search.aspx">
                Setup and search mandate
            </a>
        </Permission>
    ),
};

// @TODO Once pension calculation service is completed, need to change to real data
export const maximumIncomePaymentForRemainderOfYear = 100;
export const maximumIncomePaymentForFutureYears = 83.33;
export const maximumIncomePerPayment = 57620.75;
export const nextYearsGadMaximum = 1000;
// End @TODO Once pension calculation service is completed, need to change to real data
